import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Sparkles, Sun, Waves } from "lucide-react";
import api from "@/lib/api";
import { EventCard, ExperienceCard, GuideCard, BusinessCard } from "@/components/Cards";
import { SponsoredAd } from "@/components/SponsoredAd";
import { TID } from "@/constants/testIds";

const HERO_OPTIONS = {
  a: "https://images.unsplash.com/photo-1626070191915-0ae0d9089132?w=2000&q=85&auto=format&fit=crop", // beach + high-rises, blue sky
  b: "https://images.unsplash.com/photo-1607309843659-f4ad95cf3277?w=2000&q=85&auto=format&fit=crop", // aerial skyline + blue water
  c: "https://images.unsplash.com/photo-1691028355763-0c4144bf441b?w=2000&q=85&auto=format&fit=crop", // aerial city + ocean
  d: "https://images.unsplash.com/photo-1582761371078-6509f13666b1?w=2000&q=85&auto=format&fit=crop", // aerial skyline + turquoise
  default: "https://images.unsplash.com/photo-1626070191915-0ae0d9089132?w=2000&q=85&auto=format&fit=crop",
};
const heroParam = new URLSearchParams(window.location.search).get("hero");
const HERO_IMG = HERO_OPTIONS[heroParam] || HERO_OPTIONS.default;
const CATEGORY_STRIP = [
  { key: "surfing", label: "Surf" },
  { key: "food-drink", label: "Eats" },
  { key: "wellness", label: "Wellness" },
  { key: "family", label: "Family" },
  { key: "nightlife", label: "Nightlife" },
  { key: "luxury", label: "Luxury" },
  { key: "hinterland", label: "Hinterland" },
  { key: "hidden-gems", label: "Hidden Gems" },
];

export default function Home() {
  const [feed, setFeed] = useState({ events: [], experiences: [], guides: [], businesses: [] });
  const [search, setSearch] = useState("");
  const [flipped, setFlipped] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/home/feed").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setFlipped((f) => !f), 2500);
    return () => clearInterval(id);
  }, []);

  // Idle "wobble" nudge every 7s so visitors know the disc is tappable
  const [wobble, setWobble] = useState(false);
  useEffect(() => {
    const id = setInterval(() => { setWobble(true); setTimeout(() => setWobble(false), 900); }, 7000);
    return () => clearInterval(id);
  }, []);
  const tapFlip = () => { setFlipped((f) => !f); setWobble(false); };

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/events?q=${encodeURIComponent(search)}`);
  };

  return (
    <div>
      {/* HERO — with warm cream card overlay for readability */}
      <section className="relative px-4 lg:px-8 pt-6">
        <div className="relative overflow-hidden rounded-[2.5rem] min-h-[86vh] grain">
          <img src={HERO_IMG} alt="Gold Coast beaches and skyline at golden hour" className="absolute inset-0 w-full h-full object-cover object-[72%_center] md:object-center" />
          {/* soft gradient wash */}
          <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--ocean)]/25 via-transparent to-[color:var(--coral)]/20" />

          {/* Flipping welcome disc — G'day Mate ↔ Hey GC logo */}
          <div className="absolute z-20 top-2 left-1/2 -translate-x-1/2 md:top-14 md:right-14 md:left-auto md:translate-x-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
              className={`w-16 h-16 md:w-32 md:h-32 bob cursor-pointer select-none ${wobble ? "wobble" : ""}`}
              style={{ perspective: "900px" }}
              onClick={tapFlip}
              role="button"
              tabIndex={0}
              aria-label="Flip the welcome disc"
              title="Tap to flip"
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && tapFlip()}
              data-testid="welcome-disc"
            >
              <div
                className="relative w-full h-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{
                  transformStyle: "preserve-3d",
                  transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                {/* Front — G'day Mate (matches recoloured Hey GC logo: navy disc, cream lettering, coral sun) */}
                <div
                  className="absolute inset-0 rounded-full overflow-hidden bg-[#0f4c75] grid place-items-center text-center text-[#fbeecd] shadow-[0_20px_40px_-10px_rgba(15,76,117,0.65)] ring-2 ring-[#fbeecd]/80 ring-offset-2 ring-offset-[#0f4c75]"
                  style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                  data-testid="welcome-disc-front"
                >
                  <div className="absolute w-[58%] h-[58%] rounded-full bg-[#ff6f5e] top-[8%] left-1/2 -translate-x-1/2" />
                  <div className="absolute inset-x-0 top-[47%] h-[4%] bg-[#0f4c75]" />
                  <div className="absolute inset-x-0 top-[55%] h-[3%] bg-[#0f4c75]" />
                  <Sun size={10} className="absolute top-[10%] right-[16%] text-[#fbeecd] md:hidden" strokeWidth={3} />
                  <div className="relative leading-none pt-[18%]">
                    <div className="font-sans font-black uppercase text-[0.78rem] md:text-[1.55rem] tracking-[-0.02em] leading-[0.9] drop-shadow-[0_2px_0_rgba(15,76,117,0.9)]" style={{ transform: "scaleY(1.18)" }}>
                      G'day<br />Mate!
                    </div>
                    <div className="text-[0.3rem] md:text-[0.46rem] uppercase tracking-[0.16em] font-extrabold mt-1 md:mt-2 text-[#fbeecd]/90 whitespace-nowrap">Welcome to the Coast</div>
                  </div>
                </div>
                {/* Back — Hey GC logo */}
                <div
                  className="absolute inset-0 rounded-full overflow-hidden bg-[#0f4c75] shadow-[0_20px_40px_-10px_rgba(15,76,117,0.65)]"
                  style={{
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <img
                    src="/heygc-logo.png"
                    alt="Hey GC"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Warm cream card overlay — solves legibility */}
          <div className="relative z-10 h-full min-h-[86vh] flex items-end p-4 md:p-8 lg:p-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-[1400px] mx-auto"
            >
              <div className="hero-card-seethru text-[color:var(--hero-fg)] rounded-[2rem] p-6 pt-3 md:p-10 md:pt-4 lg:p-14 lg:pt-6 max-w-3xl shadow-[0_30px_60px_-20px_var(--hero-glow)] relative border border-white/40">
                <div className="eyebrow flex items-center gap-2 text-[color:var(--hero-accent)] mb-3">
                  <Waves size={16} />
                  <span className="text-xs md:text-sm uppercase tracking-[0.28em] font-black">Gold Coast · Australia</span>
                </div>
                <h1 className="font-display text-[2.6rem] md:text-[4.2rem] lg:text-[5.2rem] leading-[0.95] tracking-tight font-bold">
                  Sand between<br />your toes,<br />
                  <span className="italic text-[color:var(--hero-accent)]">plans</span> in your pocket.
                </h1>
                <p className="mt-6 inline-block bg-[color:var(--coral)] text-white text-sm md:text-base font-semibold leading-snug rounded-[1.75rem] px-6 py-4 md:px-7 md:py-5 max-w-xl shadow-[0_10px_28px_-10px_rgba(255,111,94,0.6)]">
                  Beach days, hinterland waterfalls, sunset dinners, hidden bars — everything the Coast does best, in one very friendly little app.
                </p>
                <form onSubmit={submitSearch} className="mt-8 max-w-2xl">
                  <div className="bg-[color:var(--shell)] rounded-full flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 pl-4 sm:pl-6 border-2 border-[color:var(--ocean)]/12 focus-within:border-[color:var(--coral)] transition-colors">
                    <Search size={18} className="text-[color:var(--ocean)] shrink-0" />
                    <input
                      data-testid={TID.heroSearch}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Surf, dinner, day-trips…"
                      className="flex-1 min-w-0 bg-transparent outline-none text-[color:var(--ink)] placeholder:text-[color:var(--ink-2)]/60 py-2.5 text-sm sm:text-base"
                    />
                    <button type="submit" data-testid={TID.heroCta} className="btn-pill btn-primary shrink-0 !px-4 sm:!px-6 !py-2.5 sm:!py-3 text-sm">
                      <span className="hidden sm:inline">Let's go</span>
                      <span className="sm:hidden">Go</span>
                      <ArrowRight size={15} className="hidden sm:inline-block" />
                    </button>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {CATEGORY_STRIP.map((c) => (
                      <Link
                        key={c.key}
                        to={`/experiences?cat=${c.key}`}
                        data-testid={TID.heroCategory(c.key)}
                        className="chip"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO tagline strip — keyword-rich H2, kept deliberately light */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-8 md:mt-10" data-testid="seo-strip">
        <div className="flex flex-col md:flex-row md:items-baseline gap-1.5 md:gap-5 border-l-2 border-[color:var(--coral)] pl-4">
          <h2 className="font-display text-base md:text-lg text-[color:var(--ocean)] whitespace-nowrap">Everything Gold Coast, All in One Place.</h2>
          <p className="text-xs md:text-sm text-[color:var(--ink-2)] leading-snug">
            Events, experiences, activities, accommodation, local businesses, exclusive deals and a personalised concierge — across the whole Gold Coast.
          </p>
        </div>
      </section>

      {/* AI CONCIERGE STRIP */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <span className="editorial-divider" />
            <div className="subhead mt-3">psst… try this</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mt-2 text-[color:var(--ocean)]">
              A <span className="italic text-[color:var(--coral)]">local mate</span> — in your pocket.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg text-[color:var(--ink-2)] leading-relaxed">
              Tell us your dates, your vibe, your budget. Our AI concierge builds a day-by-day itinerary from the coast's best beaches, kitchens and hidden corners — the way a well-connected local would.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/trip-planner" className="btn-pill btn-primary">
                <Sparkles size={15} /> Plan my trip
              </Link>
              <Link to="/concierge" className="btn-pill btn-outline">
                Chat with the concierge
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-divider" />
            <div className="subhead mt-3">what's on</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2 text-[color:var(--ocean)]">
              This month on the coast.
            </h2>
          </div>
          <Link to="/events" className="link-editorial text-sm hidden md:inline-flex">All events →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {feed.events.slice(0, 6).map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
        </div>
      </section>

      {/* EXPERIENCES — ocean band */}
      <section className="mt-32 py-24 bg-[color:var(--ocean)] text-[color:var(--sand)] rounded-[2.5rem] mx-4 lg:mx-8 relative overflow-hidden grain">
        {/* Sun */}
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[color:var(--mango)] opacity-30 blur-[2px]" />
        <div className="absolute -bottom-24 -left-16 w-96 h-96 rounded-full bg-[color:var(--coral)] opacity-20 blur-2xl" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-6">
              <span className="editorial-divider" />
              <div className="subhead mt-3 !text-[color:var(--mango)]">book direct</div>
              <h2 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">
                Experiences <span className="italic text-[color:var(--mango)]">hosted by locals.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-[color:var(--sand)]/85 leading-relaxed">
                Small-group tours, chef's tables, sunrise surf lessons, hinterland waterfalls. No middleman, no gimmicks — just people who love this place.
              </p>
              <Link to="/experiences" className="link-editorial text-sm mt-6 inline-flex !text-[color:var(--mango)]">Browse all →</Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {feed.experiences.slice(0, 4).map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}
          </div>
        </div>
      </section>

      {/* GUIDES */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-divider" />
            <div className="subhead mt-3">good reads</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2 text-[color:var(--ocean)]">The local scoop.</h2>
            <p className="text-[color:var(--ink-2)] mt-3">Guides, deep-dives and the coast's best-kept secrets.</p>
          </div>
          <Link to="/guides" className="link-editorial text-sm hidden md:inline-flex">All guides →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {feed.guides[0] && (
            <div className="md:col-span-8">
              <GuideCard guide={feed.guides[0]} size="lg" />
            </div>
          )}
          <div className="md:col-span-4 grid gap-8">
            {feed.guides.slice(1, 4).map((g, i) => <GuideCard key={g.id} guide={g} index={i} />)}
          </div>
        </div>
      </section>

      {/* DIRECTORY */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-divider" />
            <div className="subhead mt-3">tried & tested</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2 text-[color:var(--ocean)]">Where the locals go.</h2>
          </div>
          <Link to="/directory" className="link-editorial text-sm hidden md:inline-flex">Full directory →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {feed.businesses.slice(0, 4).map((b, i) => <BusinessCard key={b.id} biz={b} index={i} />)}
        </div>
      </section>

      {/* SPONSORED PARTNER AD — managed in /admin → Sponsored ads */}
      <SponsoredAd />

      {/* CTA banner */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-32 mb-8">
        <div className="rounded-[2.5rem] p-10 md:p-16 bg-[color:var(--coral)] text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[color:var(--mango)] opacity-40 blur-2xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="font-hand text-3xl mb-1 text-[color:var(--sand)]">stay in the loop</div>
              <h3 className="font-display text-4xl md:text-5xl leading-tight">Weekend guides in your inbox.</h3>
              <p className="mt-4 opacity-90 max-w-md">A weekly note from Hey GC editors — what's on, where to eat, what to skip. Zero spam. Unsubscribe anytime.</p>
            </div>
            <form onSubmit={(e) => e.preventDefault()} className="flex gap-2 bg-white/15 border border-white/30 rounded-full p-2 pl-6 backdrop-blur">
              <input type="email" placeholder="you@coastal.email" className="flex-1 bg-transparent outline-none placeholder:text-white/70 py-2.5" />
              <button type="submit" className="btn-pill btn-sunshine">
                Join <Sun size={15} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
